// src/hooks/cotizacion/useCotizacionProducts.ts
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import toast from 'react-hot-toast'
import axiosInstance from '../../api/axiosInstance'
import type { CotizacionData, Product, LineItem } from '../../types/cotizacionFormTypes'
import { calculateTotals } from '../../utils/cotizacionCalculations'
import { searchProducts } from '../../services/cotizacionService'
import { isProductInactiveInSearch } from '../../utils/productSearchUtils'
import {
  mapApiProductToOrderDetails,
  getCombinedStockDisplayOptions
} from '../../utils/productStockDisplayUtils'
import {
  isClienteNetoActive,
  applyClienteNetoToLineItems,
  lineItemSku
} from '../../utils/clienteNetoOrder'
import { useClienteNetoCotizacionSync } from './useClienteNetoCotizacionSync'

interface UseCotizacionProductsProps {
  formData: CotizacionData
  setFormData: React.Dispatch<React.SetStateAction<CotizacionData>>
  mode: 'view' | 'edit' | 'create'
  costoEnvioInputValue: string
}

export function useCotizacionProducts({
  formData,
  setFormData,
  mode,
  costoEnvioInputValue
}: UseCotizacionProductsProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [productDetails, setProductDetails] = useState<{ [key: string]: any }>({})
  const [expandedCombos, setExpandedCombos] = useState<{ [key: string]: boolean }>(() => {
    const initialExpanded: { [key: string]: boolean } = {}
    if (formData.line_items) {
      formData.line_items.forEach(item => {
        initialExpanded[item.product_id] = true
      })
    }
    return initialExpanded
  })
  const [loadingDetails, setLoadingDetails] = useState<{ [key: string]: boolean }>({})
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const isLoadingRef = useRef(false)
  const lastLineItemsRef = useRef<string>('')
  const productDetailsRef = useRef(productDetails)

  const stockDisplayOptions = useMemo(() => getCombinedStockDisplayOptions(), [])

  const { markLineItemsCnApplied, unmarkLineItemCnApplied, clearCnAppliedSkus } = useClienteNetoCotizacionSync({
    mode,
    formData,
    productDetails,
    costoEnvio: costoEnvioInputValue,
    setFormData
  })

  // Función para cargar detalles de productos
  const loadProductDetails = useCallback(async () => {
    // Evitar consultas duplicadas usando ref
    if (isLoadingRef.current) {
      console.log('Ya se están cargando productos, omitiendo consulta...')
      return
    }
    
    // Crear una clave única para los line_items actuales
    const lineItemsKey = JSON.stringify(formData.line_items?.map(item => ({
      product_id: item.product_id,
      sku: item.sku,
      quantity: item.quantity
    })) || [])
    
    // Si los line_items no han cambiado realmente, no hacer nada
    if (lastLineItemsRef.current === lineItemsKey) {
      return
    }
    
    lastLineItemsRef.current = lineItemsKey
    
    if (formData.line_items?.length) {
      isLoadingRef.current = true
      setIsLoadingProducts(true)
      const newDetails: { [key: string]: any } = {}
      const newLoadingDetails: { [key: string]: boolean } = {}
      
      // Estados que SÍ verifican stock (no agregan ?SinSaldo)
      const estadosQueVerificanStock = ['processing', 'pending', 'send_later', 'verify_transaction']
      
      // Si el estado NO está en la lista, agregar ?SinSaldo
      const agregarSinSaldo = !estadosQueVerificanStock.includes(formData.status || '')
      
      // Recopilar TODOS los SKUs necesarios (productos principales + componentes)
      const allSkus: string[] = []
      
      // Agregar SKUs de productos principales
      formData.line_items.forEach(item => {
        allSkus.push(item.sku || item.product_id)
      })
      
      // Agregar SKUs de componentes de combos
      formData.line_items.forEach(item => {
        if (item.is_combo === 1 && item.components) {
          item.components.forEach((component: any) => {
            allSkus.push(component.sku)
          })
        }
      })
      
      // Eliminar duplicados
      const uniqueSkus: string[] = Array.from(new Set(allSkus))
      
      // Marcar todos los productos como cargando
      uniqueSkus.forEach(sku => {
        newLoadingDetails[sku] = true
        setLoadingDetails(prev => ({...prev, [sku]: true}))
      })
      
      try {
        console.log('Cargando detalles de todos los productos:', uniqueSkus)
        console.log('URL de consulta múltiple:', `/products/multiple?skus=${uniqueSkus.join(',')}`)
        
        // Una sola consulta múltiple para obtener TODA la información
        let multipleUrl = `/products/multiple?skus=${uniqueSkus.join(',')}`
        if (agregarSinSaldo) {
          multipleUrl += "&SinSaldo=false"
        }
        
        console.log('URL final:', multipleUrl)
        
        const token = localStorage.getItem('accessToken')
        const response = await axiosInstance.get(multipleUrl, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.data && response.data.products) {
          // Procesar cada producto de la respuesta
          response.data.products.forEach((product: any) => {
            const sku = product.sku?.toString().replace(/^0+/, '') || ''
            const mapped = mapApiProductToOrderDetails({ ...product, sku }, stockDisplayOptions)

            newDetails[sku] = mapped

            if (product.id || product.product_id) {
              const productId = product.id || product.product_id
              newDetails[productId] = mapped
            }
          })
        }
        
        // Procesar combos para calcular stock mínimo
        formData.line_items.forEach(item => {
          console.log('Procesando combo:', item.product_id, 'sku:', item.sku, 'is_combo:', item.is_combo)
          if (item.is_combo === 1 && item.components) {
            const stocks: number[] = []
            
            // Calcular stock mínimo de componentes
            item.components.forEach((component: any) => {
              console.log('Buscando componente:', component.sku, 'en newDetails:', Object.keys(newDetails))
              const componentDetails = newDetails[component.sku]
              if (componentDetails) {
                const stockValue = componentDetails.stock || componentDetails.stock_quantity || 0
                console.log('Stock encontrado para componente', component.sku, ':', stockValue)
                stocks.push(stockValue)
              } else {
                console.log('No se encontró stock para componente:', component.sku)
                stocks.push(0)
              }
            })
            
            // Actualizar stock del combo con el mínimo
            if (stocks.length > 0) {
              const minStock = Math.min(...stocks)
              console.log('Stocks de componentes:', stocks)
              console.log('Stock mínimo para el combo:', minStock)
              
              // Actualizar por SKU del producto principal
              if (item.sku && newDetails[item.sku]) {
                console.log('Actualizando stock del combo por SKU:', item.sku, 'de', newDetails[item.sku].stock, 'a', minStock)
                newDetails[item.sku] = {
                  ...newDetails[item.sku],
                  stock: minStock,
                  stock_quantity: minStock
                }
              }
              
              // También actualizar por product_id por si acaso
              if (newDetails[item.product_id]) {
                console.log('Actualizando stock del combo por product_id:', item.product_id, 'de', newDetails[item.product_id].stock, 'a', minStock)
                newDetails[item.product_id] = {
                  ...newDetails[item.product_id],
                  stock: minStock,
                  stock_quantity: minStock
                }
              }
            }
          }
        })
        
      } catch (error) {
        console.error('Error al cargar productos múltiples:', error)
        console.warn('Intentando fallback con consultas individuales...')
        
        // 🔄 FALLBACK: CONSULTAS INDIVIDUALES
        for (const item of formData.line_items) {
          let response
          try {
            newLoadingDetails[item.product_id] = true
            setLoadingDetails(prev => ({ ...prev, [item.product_id]: true }))
            
            const token = localStorage.getItem('accessToken')
            response = await axiosInstance.get(`/products/${item.product_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
            
            if (response.data) {
              const sku = response.data.sku?.toString().replace(/^0+/, '') || ''
              newDetails[item.product_id] = mapApiProductToOrderDetails(
                {
                  ...response.data,
                  sku
                },
                stockDisplayOptions
              )
              
              // Procesar componentes si es un combo
              if (response.data.is_combo === 1 && response.data.components) {
                let stocks = []
                
                for (const component of response.data.components) {
                  const compSku = component.sku
                  // Usar ref para evitar dependencia en useCallback
                  const currentProductDetails = productDetailsRef.current
                  if (!currentProductDetails[compSku] && compSku) {
                    setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
                    
                    try {
                      const componentResponse = await axiosInstance.get(`/products/${compSku}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                      
                      if (componentResponse.data) {
                        const mappedComp = mapApiProductToOrderDetails(
                          componentResponse.data,
                          stockDisplayOptions
                        )
                        stocks.push(mappedComp.stock)
                        newDetails[compSku] = mappedComp
                      }
                    } catch (componentError) {
                      console.error('Error al cargar componente:', compSku, componentError)
                      stocks.push(0)
                    } finally {
                      setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
                    }
                  } else if (currentProductDetails[compSku]) {
                    const stockValue = currentProductDetails[compSku].stock || currentProductDetails[compSku].stock_quantity || 0
                    stocks.push(stockValue)
                  }
                }
                
                // Calcular stock mínimo para el combo
                if (stocks.length > 0) {
                  const minStock = Math.min(...stocks)
                  newDetails[item.product_id] = {
                    ...newDetails[item.product_id],
                    stock: minStock,
                    stock_quantity: minStock
                  }
                }
              }
            }
          } catch (err) {
            console.error('Error al cargar producto individual:', item.product_id, err)
            newDetails[item.product_id] = {
              ...response?.data,
              stock: 0,
              precio_ofima: null,
              iva_ofima: null,
              price: 0,
              tax_rate: 0.19
            }
          } finally {
            newLoadingDetails[item.product_id] = false
            setLoadingDetails(prev => ({ ...prev, [item.product_id]: false }))
          }
        }
      } finally {
        // Marcar todos como no cargando
        uniqueSkus.forEach(sku => {
          newLoadingDetails[sku] = false
          setLoadingDetails(prev => ({...prev, [sku]: false}))
        })
        isLoadingRef.current = false
        setIsLoadingProducts(false)
      }
      
      setProductDetails(newDetails)
      
      // Actualizar el IVA de los productos existentes en line_items
      // Usar setTimeout para evitar actualizar durante el render
      setTimeout(() => {
        setFormData(prev => {
          if (!prev.line_items) return prev
          
          const updatedLineItems = prev.line_items.map(item => {
            const productDetail = newDetails[item.sku || item.product_id]
            if (productDetail && productDetail.tax_rate !== undefined) {
              return {
                ...item,
                iva: productDetail.tax_rate.toString(),
                tax_rate: productDetail.tax_rate >= 1 ? Math.round(productDetail.tax_rate) : Math.round(productDetail.tax_rate * 100)
              }
            }
            return item
          })
          
          return {
            ...prev,
            line_items: updatedLineItems
          }
        })
      }, 0)
    } else {
      isLoadingRef.current = false
      setIsLoadingProducts(false)
    }
  }, [formData.line_items, formData.status, setFormData, stockDisplayOptions])
  
  // Actualizar ref cuando cambie productDetails
  useEffect(() => {
    productDetailsRef.current = productDetails
  }, [productDetails])

  // Cargar detalles de productos al editar cotización
  useEffect(() => {
    if (formData.line_items && formData.line_items.length > 0) {
      // Crear una clave única para los line_items
      const lineItemsKey = JSON.stringify(formData.line_items.map(item => ({
        product_id: item.product_id,
        sku: item.sku,
        quantity: item.quantity
      })))
      
      // Solo cargar si realmente cambiaron los line_items
      if (lastLineItemsRef.current !== lineItemsKey) {
        loadProductDetails()
      }
    }
  }, [formData.line_items, formData.status, loadProductDetails])

  // Función para calcular totales con productDetails
  const calculateTotalsWithProductDetails = useCallback(
    (items: any[] = [], shippingValue: string = '0') => {
      return calculateTotals(
        items,
        shippingValue,
        productDetails,
        isClienteNetoActive(formData.extraDetails)
      )
    },
    [productDetails, formData.extraDetails?.cliente_neto]
  )

  // Handler de búsqueda
  const handleSearch = useCallback(async (term: string) => {
    if (term.length >= 4) {
      setIsSearching(true)
      try {
        const products = await searchProducts(term)
        setSearchResults(products)
      } catch (err) {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    } else {
      setSearchResults([])
    }
  }, [])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    if (value.length >= 4) {
      handleSearch(value)
    } else {
      setSearchResults([])
    }
  }, [handleSearch])

  // Handler para seleccionar producto
  const handleSelectProduct = useCallback((selectedProduct: Product) => {
    if (isProductInactiveInSearch(selectedProduct)) {
      toast.error('Este producto está inactivo y no se puede agregar', { icon: '⚠️' })
      return
    }
    const newLineItems = [...(formData.line_items || [])]
    const existingItemIndex = newLineItems.findIndex(item => item.product_id === selectedProduct.product_id)
    const cleanSku = selectedProduct.sku?.toString().replace(/^0+/, '') || ''
    const productWithCleanSku = {
      ...selectedProduct,
      sku: cleanSku
    }
    
    if (existingItemIndex >= 0) {
      newLineItems[existingItemIndex].quantity = (newLineItems[existingItemIndex].quantity || 0) + 1
    } else {
      const basePrice = Number(selectedProduct.price || 0)
      // Cliente neto: el precio de catálogo ya es el valor final (no se suma IVA encima)
      const unitPrice = basePrice

      // Preparar el item con información completa
      const newItem: LineItem = {
        product_id: selectedProduct.product_id,
        product_name: selectedProduct.name || '',
        sku: cleanSku,
        display_sku: cleanSku,
        quantity: 1,
        price: unitPrice.toString(),
        iva: (selectedProduct.tax_rate || 0.19).toString(),
        tax_rate: selectedProduct.tax_rate >= 1 ? Math.round(selectedProduct.tax_rate) : Math.round((selectedProduct.tax_rate || 0.19) * 100),
        is_combo: selectedProduct.is_combo ? 1 : 0
      }
      
      // Si es un combo y tiene componentes, incluirlos
      if (selectedProduct.is_combo && selectedProduct.components) {
        newItem.components = selectedProduct.components.map(component => ({
          id_combo: parseInt(selectedProduct.product_id) || 0,
          sku: component.sku,
          quantity: component.quantity,
          name: component.name,
          price: component.price.toString(),
          tax_rate: component.tax_rate.toString()
        }))
        
        // Actualizar display_sku con el SKU del primer componente
        if (newItem.components && newItem.components.length > 0) {
          newItem.display_sku = newItem.components[0].sku
        }
      }
      
      newLineItems.push(newItem)
      
      if (selectedProduct.is_combo) {
        setExpandedCombos(prev => ({
          ...prev,
          [selectedProduct.product_id]: true
        }))
      }
      
      setProductDetails(prev => ({
        ...prev,
        [selectedProduct.product_id]: productWithCleanSku,
        [cleanSku]: productWithCleanSku
      }))

      if (isClienteNetoActive(formData.extraDetails)) {
        const idx = newLineItems.length - 1
        const detailsForCn = {
          ...productDetails,
          [selectedProduct.product_id]: productWithCleanSku,
          [cleanSku]: productWithCleanSku
        }
        const [withNeto] = applyClienteNetoToLineItems(
          [newLineItems[idx]],
          detailsForCn,
          [lineItemSku(newLineItems[idx])]
        )
        newLineItems[idx] = withNeto
        markLineItemsCnApplied([withNeto])
      }
    }
    
    const totals = calculateTotalsWithProductDetails(newLineItems, costoEnvioInputValue)
    setFormData(prev => ({
      ...prev,
      line_items: newLineItems,
      total: totals.total
    }))
    setSearchTerm('')
    setShowResults(false)
  }, [
    formData.line_items,
    formData.extraDetails?.cliente_neto,
    costoEnvioInputValue,
    calculateTotalsWithProductDetails,
    setFormData,
    productDetails,
    markLineItemsCnApplied
  ])

  // Handler para cambiar cantidad
  const handleQuantityChange = useCallback((index: number, newQuantity: number) => {
    if (!Array.isArray(formData.line_items)) return
    const newLineItems = [...formData.line_items]
    if (!newLineItems[index]) return
    if (newQuantity >= 1) {
      newLineItems[index] = { ...newLineItems[index], quantity: newQuantity || 0 }
    }
    const totals = calculateTotalsWithProductDetails(newLineItems, costoEnvioInputValue)
    setFormData(prev => ({
      ...prev,
      line_items: newLineItems,
      total: totals.total
    }))
  }, [formData.line_items, costoEnvioInputValue, calculateTotalsWithProductDetails, setFormData])

  // Handler para cambiar descuento
  const handleDiscountChange = useCallback((index: number, newDiscount: number) => {
    if (!Array.isArray(formData.line_items)) return
    const newLineItems = [...formData.line_items]
    if (!newLineItems[index]) return
    newLineItems[index] = { ...newLineItems[index], discount: newDiscount || 0 }
    const totals = calculateTotalsWithProductDetails(newLineItems, costoEnvioInputValue)
    setFormData(prev => ({
      ...prev,
      line_items: newLineItems,
      total: totals.total
    }))
  }, [formData.line_items, costoEnvioInputValue, calculateTotalsWithProductDetails, setFormData])

  // Handler para cambiar precio
  const handlePriceChange = useCallback((index: number, newPrice: number) => {
    if (!Array.isArray(formData.line_items)) return
    const newLineItems = [...formData.line_items]
    if (!newLineItems[index]) return
    newLineItems[index] = { ...newLineItems[index], price: newPrice.toString() }
    const totals = calculateTotalsWithProductDetails(newLineItems, costoEnvioInputValue)
    setFormData(prev => ({
      ...prev,
      line_items: newLineItems,
      total: totals.total
    }))
  }, [formData.line_items, costoEnvioInputValue, calculateTotalsWithProductDetails, setFormData])

  // Handler para eliminar producto
  const handleRemoveProduct = useCallback((index: number) => {
    if (!Array.isArray(formData.line_items)) return
    const newLineItems = [...formData.line_items]
    const removed = newLineItems[index]
    newLineItems.splice(index, 1)
    if (removed) {
      unmarkLineItemCnApplied(lineItemSku(removed))
    }
    const totals = calculateTotalsWithProductDetails(newLineItems, costoEnvioInputValue)
    setFormData(prev => ({
      ...prev,
      line_items: newLineItems,
      total: totals.total
    }))
  }, [
    formData.line_items,
    costoEnvioInputValue,
    calculateTotalsWithProductDetails,
    setFormData,
    unmarkLineItemCnApplied
  ])

  // Handler para toggle combo
  const toggleCombo = useCallback(async (productId: string) => {
    setExpandedCombos(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }))
    if (!expandedCombos[productId]) {
      const productDetail = productDetails[productId]
      if (productDetail?.is_combo === 1 && productDetail?.components) {
        let stocks = []
        for (const component of productDetail.components) {
          const compSku = component.sku
          if (!productDetails[compSku] && compSku) {
            setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
            try {
              const token = localStorage.getItem('accessToken')
              const response = await axiosInstance.get(`/products/${compSku}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              if (response.data) {
                const mappedComp = mapApiProductToOrderDetails(
                  response.data,
                  stockDisplayOptions
                )
                stocks.push(mappedComp.stock)
                setProductDetails(prev => ({
                  ...prev,
                  [compSku]: mappedComp
                }))
              }
            } catch (error) {
              stocks.push(0)
            } finally {
              setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
            }
          } else if (productDetails[compSku]) {
            const stockValue = productDetails[compSku].stock || productDetails[compSku].stock_quantity || 0
            stocks.push(stockValue)
          }
        }
        if (stocks.length > 0) {
          const minStock = Math.min(...stocks)
          setProductDetails(prev => ({
            ...prev,
            [productId]: {
              ...prev[productId],
              stock: minStock,
              stock_quantity: minStock
            }
          }))
        }
      }
    }
  }, [expandedCombos, productDetails, stockDisplayOptions])

  return {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    productDetails,
    expandedCombos,
    loadingDetails,
    isLoadingProducts,
    handleSearchChange,
    handleSelectProduct,
    handleQuantityChange,
    handleDiscountChange,
    handlePriceChange,
    handleRemoveProduct,
    toggleCombo,
    setShowResults,
    calculateTotalsWithProductDetails,
    markLineItemsCnApplied,
    clearCnAppliedSkus
  }
}

